import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';
import { FinalDecisionCodes, ResponseCodes } from '../types/responseCodes';
import { configs } from '@constants/configs/configs';
import { useAppDispatch } from '@hooks/index';
import { setResponseBasedErrorMessage } from '@actions/ridp/ridpActionCreators';
import { history } from '@store/appStore';

export function useHandleRIDPResponse(ridpPrimaryResponse, userInfo, setErrorMessage, isRidpRbaEnabled) {
    const navigate = useNavigate();
    const existingParams = new URLSearchParams(window.location.search);

    const {
        routes: { RIDP_IDENTITY_QUESTION, RIDP_CALL_EXPERIAN, RIDP_UPLOAD_DOCUMENT, RIDP_TECHNICAL_ERROR },
    } = configs;

    const handleResponseCode = responseCode => {
        const errorMappings = {
            [ResponseCodes.HE200013]: 'ridp.contactinfo.ssnerror',
            [ResponseCodes.HE200037]: 'ridp.contactinfo.ssnerror',
            [ResponseCodes.HE200014]: 'ridp.contactinfo.addresserror',
            [ResponseCodes.HE200015]: 'ridp.contactinfo.streetaddresserror',
            [ResponseCodes.HE200024]: 'ridp.contactinfo.stateerror',
            [ResponseCodes.HE200025]: 'ridp.contactinfo.suffixerror',
            [ResponseCodes.HE200026]: 'ridp.contactinfo.doberror',
            [ResponseCodes.HE200027]: 'ridp.contactinfo.middlenameerror',
        };

        switch (responseCode) {
            case ResponseCodes.HE000050:
                if (['RF1', 'RF2'].some(code => ridpPrimaryResponse.verificationResponse.finalDecision.includes(code),)) {
                    navigate(`${RIDP_CALL_EXPERIAN}?${existingParams.toString()}`);
                } else if (userInfo.sixteenHourBlock) {
                    navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`); // Usage limit error page
                } else {
                    navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`); // Redirect to document upload page
                }
                break;
            case ResponseCodes.HS000000:
                navigate(`${RIDP_IDENTITY_QUESTION}?${existingParams.toString()}`);
                break;
            case ResponseCodes.HE200001:
            case ResponseCodes.HE200002:
            case ResponseCodes.HE200003:
            case ResponseCodes.HE200029:
            case ResponseCodes.HE200030:
            case ResponseCodes.HE200031:
            case ResponseCodes.HE200038:
            case ResponseCodes.HE200039:
                navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`);
                break;

            default:
                if (errorMappings[responseCode]) {
                    setErrorMessage(errorMappings[responseCode]);
                } else {
                    navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
                }
                break;
        }
    };

    useEffect(() => {
        if (!isRidpRbaEnabled) {
            if (ridpPrimaryResponse?.ResponseMetadata) {
                const { ResponseCode } = ridpPrimaryResponse.ResponseMetadata;
                handleResponseCode(ResponseCode);
            } else if (ridpPrimaryResponse && Object.keys(ridpPrimaryResponse).length) {
                navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
            } else if (ridpPrimaryResponse === null) {
                navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
            }
        }
    }, [ridpPrimaryResponse]);
}

export function useHandleRIDPSecondaryResponse(ridpSecondaryResponse) {
    const navigate = useNavigate();
    const existingParams = new URLSearchParams(window.location.search);
    const {
        routes: {
            RIDP_CALL_EXPERIAN,
            RIDP_UPLOAD_DOCUMENT,
            RIDP_CONTACT_INFO,
            RIDP_TECHNICAL_ERROR,
            RIDP_VERIFICATION_COMPLETE,
        },
    } = configs;
    const dispatch: Dispatch<any> = useAppDispatch();

    const handleRedirect = (finalDecisionCode) => {
        if (['RF1', 'RF2'].some(code => finalDecisionCode.includes(code))) {
            navigate(`${RIDP_CALL_EXPERIAN}?${existingParams.toString()}`); // Call Experian
        } else if (finalDecisionCode === 'RF3') {
            navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`); // Usage limit error page
        } else if (finalDecisionCode === 'RF4') {
            navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`); // Redirect to document upload page
        } else {
            navigate(`${RIDP_VERIFICATION_COMPLETE}?${existingParams.toString()}`); // Finish tab
        }
    };

    useEffect(() => {
        if (ridpSecondaryResponse?.verificationResponse?.ResponseMetadata || ridpSecondaryResponse?.ResponseMetadata) {
            const ResponseMetadata = ridpSecondaryResponse.verificationResponse.ResponseMetadata || ridpSecondaryResponse?.ResponseMetadata;
            const { ResponseCode } = ResponseMetadata;
            switch (ResponseCode) {
                case ResponseCodes.HE000050:
                    handleRedirect(
                        ridpSecondaryResponse?.verificationResponse?.finalDecision,
                    );
                    break;

                case ResponseCodes.HS000000:
                    handleRedirect(
                        ridpSecondaryResponse?.verificationResponse?.finalDecision,
                    );
                    break;

                case ResponseCodes.HE200016:
                case ResponseCodes.HE200040:
                    dispatch(setResponseBasedErrorMessage('ridp.identityquestions.incorrectanswererror'));
                    navigate(`${RIDP_CONTACT_INFO}?${existingParams.toString()}`);
                    break;

                case ResponseCodes.HE200018:
                case ResponseCodes.HE200041:
                    dispatch(setResponseBasedErrorMessage('ridp.identityquestions.sessionerror'));
                    navigate(`${RIDP_CONTACT_INFO}?${existingParams.toString()}`);
                    break;

                default:
                    dispatch(setResponseBasedErrorMessage('ridp.identityquestions.technicalerror'));
                    break;
            }
        } else if (ridpSecondaryResponse === null) {
            navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
        } else if (Object.keys(ridpSecondaryResponse).length) {
            navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
        }
    }, [ridpSecondaryResponse, history]);
}

export function useHandleValidateFARSResponse(FARSResponse, setErrorMessage, isRidpRbaEnabled) {
    const navigate = useNavigate();
    const existingParams = new URLSearchParams(window.location.search);
    const {
        routes: { RIDP_UPLOAD_DOCUMENT, RIDP_TECHNICAL_ERROR, RIDP_VERIFICATION_COMPLETE },
    } = configs;

    const handleRedirectBasedOnFinalDecisionCode = (finalDecisionCode) => {
        if (finalDecisionCode === 'RF3') {
            navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`); // Usage limit error page
        } else if (finalDecisionCode === 'RF4') {
            navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`); // Redirect to document upload page
        } else {
            navigate(`${RIDP_VERIFICATION_COMPLETE}?${existingParams.toString()}`); // Finish tab
        }
    };

    useEffect(() => {
        if (FARSResponse?.FinalDecisionCode && !isRidpRbaEnabled) {
            if (['RF1', 'RF2'].indexOf(FARSResponse.FinalDecisionCode) !== -1) {
                setErrorMessage('ridp.callExperian.description.errorMsg2');
            } else if (FARSResponse.FinalDecisionCode === 'RF3') {
                handleRedirectBasedOnFinalDecisionCode(FARSResponse.FinalDecisionCode); //'Usage limit page' error
            } else if (FARSResponse.FinalDecisionCode === 'RF4') {
                //RF4
                handleRedirectBasedOnFinalDecisionCode(FARSResponse.FinalDecisionCode); //'Redirect to document upload page'
            } else if (FARSResponse?.ResponseMetadata) {
                switch (FARSResponse.ResponseMetadata.ResponseCode) {
                    case ResponseCodes.HS000000:
                        navigate(`${RIDP_VERIFICATION_COMPLETE}?${existingParams.toString()}`);
                        break;
                    case ResponseCodes.HE200006:
                        setErrorMessage('ridp.callExperian.description.errorMsg1');
                        break;
                    case ResponseCodes.HE200045:
                        setErrorMessage('ridp.callExperian.description.errorMsg2');
                        break;
                    case ResponseCodes.HE200004:
                    case ResponseCodes.HE200005:
                    case ResponseCodes.HE200008:
                    case ResponseCodes.HE200009:
                    case ResponseCodes.HE200010:
                    case ResponseCodes.HE200016:
                    case ResponseCodes.HE200018:
                    case ResponseCodes.HE200020:
                        setErrorMessage('ridp.identityquestions.technicalerror');
                        break;

                    default:
                        setErrorMessage('ridp.identityquestions.technicalerror');
                        break;
                }
            } else {
                navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
            }
        }
    }, [FARSResponse, history]);
}

export function useHandleRIDPRBAResponse(ridpRbaResponse, userInfo, setErrorMessage, isRidpRbaEnabled) {
    const navigate = useNavigate();
    const existingParams = new URLSearchParams(window.location.search);

    const {
        routes: { RIDP_CALL_EXPERIAN, RIDP_UPLOAD_DOCUMENT, RIDP_TECHNICAL_ERROR, RIDP_VERIFICATION_COMPLETE },
    } = configs;

    const handleRedirect = (finalDecisionCode) => {

        switch (finalDecisionCode) {
            case FinalDecisionCodes.CONTINUE:
                navigate(`${RIDP_VERIFICATION_COMPLETE}?${existingParams.toString()}`); // Finish tab
                break;
            case FinalDecisionCodes.INVESTIGAT:
                navigate(`${RIDP_CALL_EXPERIAN}?${existingParams.toString()}`); // Call Experian
                break;
            case FinalDecisionCodes.STOP:
                navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`); // Manual Document Upload page
                break;
            case FinalDecisionCodes.ERROR:
            case FinalDecisionCodes.NODECISION:
                navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`); // Technical Error page
                break;
        }
    };

    const handleError = (responseCode, setErrorMessage) => {
        switch (responseCode) {
            case ResponseCodes.HE200006:
                setErrorMessage('ridp.callExperian.description.errorMsg1');
                break;
            case ResponseCodes.HE200045:
                setErrorMessage('ridp.callExperian.description.errorMsg2');
                break;
            case ResponseCodes.HE200004:
            case ResponseCodes.HE200005:
            case ResponseCodes.HE200008:
            case ResponseCodes.HE200009:
            case ResponseCodes.HE200010:
            case ResponseCodes.HE200016:
            case ResponseCodes.HE200018:
            case ResponseCodes.HE200020:
                setErrorMessage('ridp.identityquestions.technicalerror');
                break;
        }
    };

    useEffect(() => {
        if (isRidpRbaEnabled) {
            if (ridpRbaResponse && Object.keys(ridpRbaResponse).length) {
                if (ridpRbaResponse?.dvsVerificationResponse) {
                    const { finalDecisionCode } = ridpRbaResponse.dvsVerificationResponse;
                    handleRedirect(finalDecisionCode);
                    handleError(ridpRbaResponse.dvsVerificationResponse.responseCode, setErrorMessage);

                } else {
                    navigate(`${RIDP_TECHNICAL_ERROR}?${existingParams.toString()}`);
                }
            } else if (userInfo.ridpVerified) {
                navigate(`${RIDP_VERIFICATION_COMPLETE}?${existingParams.toString()}`);
            }
        }
    }, [ridpRbaResponse]);
}